import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { FinancialCalendarEntity } from '@/entities';

import type { CalendarFilter } from './types';
import { VALID_IMPACTS, shouldSendCountryParam } from './utils';

const PAGE_SIZE = 20;
const CALENDAR_PAGE_SIZE = 500;

const buildFilterParams = (filter: CalendarFilter) => ({
  from: filter.from,
  to: filter.to,
  // 🔴 TRAD-2141 — category 는 **항상 명시해서 보낸다.**
  // 원래는 "전체 선택 = 파라미터 생략 → 서버 전체 반환" 이었고, 클라의 '전체'와 서버의 '전체'가
  // 같다는 전제 위에서만 성립한다. CRYPTO 를 숨기면서 그 전제가 깨졌다 —
  // 파라미터를 생략하면 서버는 **CRYPTO 를 포함한 4개 전체**를 돌려주므로, 칩은 없는데
  // 과거 날짜에서 코인 이벤트가 그대로 보인다(운영 실측: 7/10~7/25 무파라미터 1646건 중 89건이 CRYPTO,
  // 명시 3개는 1557건 = 1646−89). 미래는 0건이라 안 보이지만 캘린더는 과거로 이동할 수 있다.
  // 복구(TRAD-2129) 시 이 3줄을 아래 주석의 원래 조건으로 되돌린다:
  //   filter.categories.length > 0 && filter.categories.length < VALID_CATEGORIES.length ? filter.categories : undefined
  category: filter.categories.length > 0 ? filter.categories : undefined,
  // TRAD-2310 — 조건을 여기 복사해 두지 말 것. 필터바의 "기타 포함" 배지가 같은 함수를 쓰므로
  // 한쪽만 바뀌면 화면과 요청이 갈라진다.
  country: shouldSendCountryParam(filter.countries) ? filter.countries : undefined,
  importance:
    filter.impacts.length > 0 && filter.impacts.length < VALID_IMPACTS.length
      ? filter.impacts
      : undefined,
});

const isEnabled = (filter: CalendarFilter) =>
  filter.categories.length > 0 &&
  filter.countries.length > 0 &&
  filter.impacts.length > 0;

// TRAD-2071: 날짜별 {highCount,totalCount} 집계 — importance와 무관(그 필드 자체를 서버에 보내지 않음).
// category/country가 전부 해제된 상태에서만 비활성(목록 자체가 빈 상태와 동일 조건).
const buildDailyCountsParams = (filter: CalendarFilter) => ({
  from: filter.from,
  to: filter.to,
  // TRAD-2141 — 위 buildFilterParams 와 같은 이유로 항상 명시. 여기서 생략하면 월뷰 날짜 뱃지의
  // 개수만 CRYPTO 를 포함해 목록과 어긋난다(목록 3건인데 뱃지는 5건).
  category: filter.categories.length > 0 ? filter.categories : undefined,
  // TRAD-2310 — 조건을 여기 복사해 두지 말 것. 필터바의 "기타 포함" 배지가 같은 함수를 쓰므로
  // 한쪽만 바뀌면 화면과 요청이 갈라진다.
  country: shouldSendCountryParam(filter.countries) ? filter.countries : undefined,
});

const isDailyCountsEnabled = (filter: CalendarFilter) =>
  filter.categories.length > 0 && filter.countries.length > 0;

export const financialCalendarOptions = {
  rootKey: 'financial-calendar',

  /** 리스트 뷰: 무한스크롤 (pageSize=20) */
  getEventList: (filter: CalendarFilter) =>
    infiniteQueryOptions({
      queryKey: [
        financialCalendarOptions.rootKey,
        'events',
        'list',
        filter.from,
        filter.to,
        filter.categories,
        filter.countries,
        filter.impacts,
      ],
      enabled: isEnabled(filter),
      queryFn: ({ pageParam }) =>
        FinancialCalendarEntity.fetchFinancialCalendarEvents({
          ...buildFilterParams(filter),
          page: pageParam,
          pageSize: PAGE_SIZE,
        }),
      initialPageParam: 1,
      // TRAD-2452 — fetchFinancialCalendarEvents가 이제 빈 페이지로 폴백하니 lastPage가
      // undefined일 일은 없지만, 이 계약이 다시 깨져도 여기서 크래시하지 않도록 방어한다
      // (getNextPageParam은 react-query가 잡는 쿼리 에러가 아니라 렌더 중 예외가 되어
      // 라우터 errorElement로 페이지 전체를 날린다 — 실측 확인, TRAD-2452).
      getNextPageParam: (lastPage, pages) => (!lastPage || lastPage.last ? undefined : pages.length + 1),
    }),

  /** 캘린더 뷰(주간/월간): 단일 쿼리 (pageSize=500, 전체 조회) */
  getCalendarEvents: (filter: CalendarFilter) =>
    queryOptions({
      queryKey: [
        financialCalendarOptions.rootKey,
        'events',
        'calendar',
        filter.from,
        filter.to,
        filter.categories,
        filter.countries,
        filter.impacts,
      ],
      enabled: isEnabled(filter),
      queryFn: () =>
        FinancialCalendarEntity.fetchFinancialCalendarEvents({
          ...buildFilterParams(filter),
          page: 1,
          pageSize: CALENDAR_PAGE_SIZE,
        }),
    }),

  /** TRAD-2071: 날짜별 {highCount,totalCount} 집계 — "전체 M건 보기" 고지용. /events와 분리된 경량 엔드포인트. */
  getDailyCounts: (filter: CalendarFilter) =>
    queryOptions({
      queryKey: [
        financialCalendarOptions.rootKey,
        'events',
        'daily-counts',
        filter.from,
        filter.to,
        filter.categories,
        filter.countries,
      ],
      enabled: isDailyCountsEnabled(filter),
      queryFn: () => FinancialCalendarEntity.fetchFinancialCalendarDailyCounts(buildDailyCountsParams(filter)),
    }),
};
